import { colors } from 'baseui/tokens';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import Text from 'components/elements/text';
import { format } from 'date-fns';
import { SeparatorHorizontal, SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import {
  HotelContainer,
  HotelContent,
  Container,
  GuestContent,
  TotalContent,
  TotalItemContent,
} from './styles';
import {
  calculateHotelPurchaseAddons,
  calculateHotelPurchaseDetail,
} from '../../hooks/use-recalculate';
import { HotelPurchaseItemFormType } from '../form-type';

interface Props {
  parentIdx: number;
  data: HotelPurchaseItemFormType;
}

export default function SummaryItem(props: Props) {
  const { t } = useTranslation();
  const { parentIdx, data } = props;
  const isByPax = data.isByPax;
  const subtotalAddons = calculateHotelPurchaseAddons(data.hotelPurchaseAddOns);
  const subtotalDetail = calculateHotelPurchaseDetail(
    data.hotelPurchaseHotels,
    isByPax,
  );
  const subtotal = subtotalAddons + subtotalDetail;
  return (
    <Container>
      <>
        <Text
          variant="LabelMedium"
          $style={{ color: stylesColors.primaryDefault }}
        >
          {t('ticketOrders:schedule_extra', {
            extra: `${parentIdx + 1}`,
          })}
        </Text>

        <GuestContent>
          <Text variant="LabelSmall">
            {data.hotelPurchaseGuests
              .map((guest) => guest.guestName)
              .join(', ')}
          </Text>
        </GuestContent>
        {data.hotelPurchaseHotels.map((hotel, idx) => {
          const night = hotel.night || 0;
          const rooms = hotel.room || 0;

          const nightText = `${night} ${t(
            `common:${night > 1 ? 'nights' : 'night'}`,
          ).toUpperCase()}`;
          const roomText = `${rooms} ${t(
            `common:${rooms > 1 ? 'rooms' : 'room'}`,
          ).toUpperCase()}`;

          const subtotal = calculateHotelPurchaseDetail([hotel], isByPax);

          return (
            <HotelContainer key={hotel.id || `${parentIdx}-hotel-${idx}`}>
              <HotelContent>
                <Text variant="LabelSmall">{`${
                  hotel.checkIn ? format(hotel.checkIn, 'dd-MM-yyyy') : '-'
                } - ${
                  hotel.checkOut ? format(hotel.checkOut, 'dd-MM-yyyy') : '-'
                } (${nightText}) | ${roomText} | ${(
                  hotel.hotelRoomName || '-'
                ).toUpperCase()}`}</Text>
                <SeparatorHorizontal $margin={8} />
                <Text variant="LabelSmall" $style={{ color: colors.gray500 }}>
                  {`Rp ${string2money(hotel.price || 0)}`}
                </Text>
              </HotelContent>
              <Text variant="LabelSmall">{`Rp ${string2money(subtotal)}`}</Text>
            </HotelContainer>
          );
        })}
        {!!data.hotelPurchaseAddOns.length && (
          <Container>
            <SeparatorVertical $margin={8} />
            <Text variant="ParagraphSmall" $style={{ color: colors.gray500 }}>
              {t('ticketOrders:add_ons')}
            </Text>
            {data.hotelPurchaseAddOns.map((addon, idx) => (
              <HotelContainer
                key={addon.id || `${parentIdx}-hotelPurchaseAddOns-${idx}`}
              >
                <GuestContent>
                  <Text variant="LabelSmall">
                    {`${addon.guestName || '-'} | ${addon.description || '-'}`}
                  </Text>
                  <SeparatorHorizontal $margin={8} />
                  <Text variant="LabelSmall" $style={{ color: colors.gray500 }}>
                    {`@ Rp ${string2money(addon.price || 0)}`}
                  </Text>
                </GuestContent>
                <Text variant="LabelSmall">
                  {`Rp ${string2money(addon.price || 0)}`}
                </Text>
              </HotelContainer>
            ))}
          </Container>
        )}
        <SeparatorVertical $margin={24}>
          <div style={{ height: 1, backgroundColor: colors.gray200 }} />
        </SeparatorVertical>
      </>
    </Container>
  );
}
